import React, {
  memo, useCallback, useMemo,
} from 'react'
import {
  useInfiniteQuery,
} from 'react-query'
import { useRouter } from 'next/router'
import {
  FormattedMessage,
} from 'react-intl'
import Grid from '@mui/material/Grid2'
import LinearProgress from '@mui/material/LinearProgress'
import Box from '@mui/material/Box'
import useUtms from 'hooks/useUtms'
import Contact from 'components/shared/Contact'
import { getFilterObject } from 'filters'
import VehicleCardVariant from 'components/SRP/VehicleCardVariant'
import { getVehicles } from 'utils/universal/getVehicles'
import { getVehicleLink } from 'utils/vehicles'
import usePageProps from 'hooks/usePageProps'
import useIntersectionObserver from 'hooks/useIntersectionObserver'
import VehicleMobileCard from 'components/SRP/VehiclMobileCard'
import { Button, Typography } from '@mui/material'
import envs from 'envs'
import EmptyState from 'components/shared/EmptyState/EmptyState'
import EmptyImageSrp from 'public/img/la_car.svg'
import useNonInitialEffect from 'hooks/useNonInitialEffect'
import EventEmitter from 'services/EventEmitter'
import pickBy from 'lodash/pickBy'
import { useMobile } from 'hooks/useMobile'
import sx from './styles'

const monthsToFinancing = 60
const credit = 'Si'
const goodCreditHistory = 'No'

const VehicleList = (props) => {
  const {
    sx: sxProp,
  } = props
  const {
    isDraftEasy,
    b2bVehicles,
    makers,
    isMobile: isMobileServer,
  } = usePageProps()
  const router = useRouter()
  const filters = useMemo(() => getFilterObject(router.query?.params), [router.query?.params])
  const query = useUtms()
  const vehiclesPerPage = envs?.vehiclesPerPage
  // const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'))
  const isMobile = useMobile(isMobileServer)
  const VehicleCardComponent = useMemo(
    () => (isMobile ? VehicleMobileCard : VehicleCardVariant), [isMobile],
  )
  let formattedFilters = { ...filters }
  if (filters?.bonus) {
    delete formattedFilters.bonus
    formattedFilters = { ...formattedFilters, has_bonus: true }
  }

  const {
    status,
    data,
    error,
    isFetching,
    isFetchingNextPage,
    // isFetchingPreviousPage,
    fetchNextPage,
    // fetchPreviousPage,
    hasNextPage,
    // hasPreviousPage,
    // refetch,
  } = useInfiniteQuery(
    ['vehicles', router.query?.params],
    ({ pageParam = 0 }) => getVehicles(
      {
        ...formattedFilters,
        page: pageParam,
      },
      null,
      vehiclesPerPage,
      isDraftEasy,
      makers,
    ),
    {
      staleTime: 1000 * 60 * 60,
      refetchOnWindowFocus: false,
      refetchOnmount: false,
      refetchOnReconnect: false,
      retry: false,
      keepPreviousData: true,
      getPreviousPageParam: (firstPage) => firstPage.pagination.previousPage ?? false,
      getNextPageParam: (lastPage) => lastPage.pagination.nextPage ?? false,
    },
  )

  const loadMoreButtonRef = React.useRef()

  useIntersectionObserver({
    target: loadMoreButtonRef,
    onIntersect: fetchNextPage,
    enabled: hasNextPage,
  })

  const loadMoreButtonText = isFetchingNextPage
    ? 'LOADING_MORE' : hasNextPage
      ? 'LOAD_MORE'
      : 'END_OF_RESULTS'

  const mergeVehiclesWithBanners = useCallback((vehicles) => {
    if (!vehicles) return []
    // // EXPERIMENT 1 SRP
    // if (data?.pages?.length === 1) {
    //   const items = [...vehicles]
    //   if (showMobileCardVariant) {
    //     items.splice(2, 0, {
    //       type: 'card',
    //     })
    //     return items
    //   }
    //   // END EXPERIMENT 1 SRP
    // }
    return vehicles
  }, [data, isMobile, data?.pages])

  const dataAutosCount = data?.pages[0].inventory.length <= 0

  // const renderExtraModule = (banner, index) => (
  //   <Grid
  //     key={`banner-${index}`}
  //     item
  //     xs={12}
  //     sm={12}
  //     md={6}
  //     lg={3}
  //     sx={{ paddingTop: '0px!important' }}
  //   >
  //     {/* <Quoter
  //       title="SEARCH_LIST_BANER_FORM_QUOTER"
  //       buttonId="srp_componente_cotizador"
  //       variant="column"
  //       isCard
  //       sx={sx.quoter}
  //     /> */}
  //     <SearchDemoVehicles variant="filter" />
  //   </Grid>
  // )

  const renderCarNearOfYouTitle = () => {
    return (
      <Grid
        size={12}
        my={2}
      >
        <Box sx={{ width: '100%', pl: { xs: 1, lg: 0 } }}>
          <Typography
            variant="strong"
            component="p"
          >
            <FormattedMessage id="SEARCH_SRP_CARS_NEAR_OF_YOU" />
          </Typography>
        </Box>
      </Grid>
    )
  }

  const renderItem = useCallback((item, index) => {
    // if (item?.type === 'card') {
    //   return renderExtraModule(item, index)
    // }

    if (item?.type === 'nearOfYouTitle') {
      if (item?.visible) {
        return renderCarNearOfYouTitle(item, index)
      }
      return ''
    }

    return (
      <Grid
        key={`vehicle-${item.id}`}
        size={{
          xs: 12,
          sm: 12,
          md: 6,
          lg: 4,
        }}
      >
        <VehicleCardComponent
          swiper={!isMobile}
          direction={isMobile ? 'row' : 'column'}
          vehicle={item}
          showPayments
          href={`${getVehicleLink(item)}${query}`}
          priority={index < 2}
          index={index + 1}
          isB2BVehicle={(b2bVehicles?.includes(item?.id) || item?.salesChannel === 'b2b_b2c') ?? false}
          monthsToFinancing={monthsToFinancing}
          credit={credit}
          goodCreditHistory={goodCreditHistory}
        />
      </Grid>
    )
  }, [query, isMobile])

  const btn = {
    variant: 'special',
    redirect: 'ROUTE_SEARCH',
    cta: 'SYC_EMPTY_STATE_CTA',
  }

  const dataListSize = data?.pages[0].inventory.length

  useNonInitialEffect(() => {
    if (data?.pages) {
      EventEmitter.emit('LOAD_MORE')
    }
  }, [data])

  // Se lanza evento para trackear FilterApplied
  useNonInitialEffect(() => {
    if (!isFetching && data?.pages) {
      if (typeof window !== 'undefined') {
        if (window.dataLayer) {
          window.dataLayer.push({
            event: 'FilterApplied',
            filters: JSON.stringify(pickBy(filters) || {}),
            total: data?.pages[0]?.pagination?.totalEntries,
          })
        }
      }
    }
  }, [isFetching, data?.pages])

  if (status === 'success') {
    return (
      <>
        {
          (dataAutosCount) && (
            <EmptyState
              title="SYC_EMPTY_STATE_SUBTITLE"
              text="SYC_EMPTY_STATE_TITLE"
              btn={btn}
              image={<EmptyImageSrp />}
            />
          )
        }

        <Box sx={{ ...sx.root, ...sxProp }}>
          <Grid
            container
            justifyContent="center"
            flexDirection="column"
            alignItems="center"
          >
            <Grid
              size={12}
              container
              spacing={isMobile ? 1 : 2}
            >
              {data?.pages?.map((pageData) => (
                <React.Fragment key={pageData.pagination.nextPage}>
                  <>
                    {
                      mergeVehiclesWithBanners([
                        ...pageData?.inventory,
                        {
                          type: 'nearOfYouTitle',
                          visible: pageData?.pagination?.nextPage === null
                            && pageData?.extra_inventory?.length > 0,
                        },
                        ...(
                          pageData?.pagination?.nextPage === null
                            ? pageData?.extra_inventory || []
                            : []
                        ),
                      ]).map(
                        (item, index) => renderItem(item, index),
                      )
                    }
                  </>
                </React.Fragment>
              ))}
            </Grid>

            {
              (dataListSize !== 0) && (
                <Grid
                  size={{
                    xs: 11,
                    sm: 6,
                    lg: 3,
                  }}
                  my={2}
                >
                  <Button
                    id="btn-cargar-mas-autos"
                    variant="special"
                    ref={loadMoreButtonRef}
                    onClick={() => fetchNextPage()}
                    disabled={!hasNextPage || isFetchingNextPage}
                    fullWidth
                    sx={sx.loadMoreButton}
                  >
                    <FormattedMessage id={loadMoreButtonText} />
                  </Button>
                </Grid>
              )
            }
          </Grid>
        </Box>
      </>
    )
  }

  if (isFetching) {
    return (
      <Box sx={{ ...sx.root, ...sxProp }}>
        <LinearProgress color="primary" />
      </Box>
    )
  }

  if (error) {
    return (
      <Contact
        title="API_ERROR_MESSAGE"
        description="CONTACT_INFO"
        showInfo={false}
        sx={{
          maxWidth: 500,
          background: 'none',
          '& .MuiButton-root': {
            width: 'max-content',
          },
        }}
      />
    )
  }

  return null
}
export default memo(VehicleList)
